import '../../styles/restScreens.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    DndContext, useDraggable, useDroppable,
    PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { supabaseClient } from '../../config/supabase';
import { TONE_CLASS, toneTextStyle } from '../clients/clientsConstants';
import useModuleAccess from '../../hooks/useModuleAccess';

// Module 2 — the Pipeline Kanban (Gate 11 · UX Addendum §4b · Pattern Library entry #3).
//
// Operator-configurable stage columns with draggable cards and per-column tallies. Columns
// come from pipeline_stages ordered by rank — RENAME THEM FREELY. Everything here branches
// on stage.type (open | won | converted | lost), never on the column's NAME, because the name
// is the tenant's word: The Ride's converted column reads "Reservations", a law firm's would
// read "Matters". Match on the label and the board breaks the day someone renames it.
//
// THE CONVERTED COLUMN IS NOT A DROP TARGET. Whether the job exists is a fact, not an
// opinion: a card lands there only when its engagement exists, placed by the trigger from
// migration 018 off won_engagement_id. Dragging into it is refused at the database too
// (hand-placement RAISES), so this is the UI agreeing with the rule rather than inventing it.
//
// Entitlement: opportunities' RLS is gated by has_capability('crm.pipeline'), so a tenant
// without the key already sees zero rows. CrmScreen hides the section as well — the UI gate
// is courtesy; the data layer is the enforcement.

// Two views, one switcher — the same grammar the Clients section uses.
//
// Board is the default: it is the view you MANIPULATE (drag a deal to move it). List is the
// view you JUDGE from, and it carries the one thing a board structurally cannot show —
// DAYS IN STAGE. On a board, a deal parked in Quote Sent for 90 days looks identical to one
// that arrived yesterday; that is the commonest pipeline pathology and a column cannot
// express it. stage_changed_at (stamped by 018's trigger on every move) makes it free.
// A board is the workshop floor. A list is the clipboard.
const PIPELINE_VIEWS = ['Board', 'List'];
const PIPELINE_VIEW_KEY = 'crm.pipeline.viewMode';
const STALE_DAYS = 30;   // past this, a card is flagged — see the note in PipelineList

const money = (n) =>
    n == null ? null : new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(n);

const daysSince = (iso) =>
    iso == null ? null : Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

const OPP_SELECT = `
    id, org_id, entity_id, owner_id, account_id, contact_id, stage_id,
    value, expected_close, source, status, won_engagement_id, stage_changed_at,
    account:accounts!opportunities_account_id_fkey ( id, account_name, account_number ),
    contact:contacts!opportunities_contact_id_fkey ( id, name )
`;
const OPP_SELECT_NO_VALUE = OPP_SELECT.replace('    value, ', '    ');

const stageTone = (type) =>
    type === 'won' ? 'good' : type === 'lost' ? 'muted' : type === 'converted' ? 'system' : 'open';

export const stageValueTotal = (opportunities) =>
    opportunities.reduce((sum, opportunity) => sum + (Number(opportunity.value) || 0), 0);

export const stageMovePayload = (stage) => ({
    stage_id: stage.id,
    status: stage.type === 'won' ? 'won' : stage.type === 'lost' ? 'lost' : 'open',
});

function OpportunityCard({ opp, onCreateReservation, canEdit = true, showFinancialValues = true }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: opp.id, disabled: !canEdit });
    const style = transform
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
        : undefined;

    const panel = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const needsReservation = opp.stageType === 'won' && !opp.won_engagement_id;

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...listeners}
            {...attributes}
            className={`rounded-lg border p-3 shadow-sm select-none ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}
                        ${panel} ${isDragging ? 'opacity-50' : ''}`}
        >
            <div className="font-medium text-sm truncate text-[var(--ink)]">
                {opp.account?.account_name || 'No account'}
            </div>
            {opp.contact?.name && (
                <div className="text-xs truncate text-[var(--ink-muted)]">
                    {opp.contact.name}
                </div>
            )}
            <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-[var(--ink)]">
                    {showFinancialValues ? (money(opp.value) || <span className="text-gray-500 font-normal">No value</span>) : null}
                </span>
                {showFinancialValues && opp.expected_close && (
                    <span className="text-[11px] text-[var(--ink-muted)]">
                        {opp.expected_close}
                    </span>
                )}
            </div>

            {/* Won, but never turned into work — the signal the sixth column exists to surface. */}
            {canEdit && needsReservation && (
                <button
                    onPointerDown={e => e.stopPropagation()}
                    onClick={() => onCreateReservation(opp)}
                    className="mt-2 w-full py-1 rounded text-[11px] font-semibold bg-brand-600 text-white hover:bg-brand-700"
                >
                    Create Reservation →
                </button>
            )}
            {opp.won_engagement_id && (
                <div className="mt-2 text-[11px] font-mono truncate" style={toneTextStyle('system')}>
                    → job created
                </div>
            )}
        </div>
    );
}

function StageColumn({ stage, opps, onCreateReservation, canEdit, showFinancialValues }) {
    // A converted column takes no drops: the database refuses hand-placement, so the board
    // must not offer it. dnd-kit's `disabled` keeps it from ever highlighting as a target.
    const isConverted = stage.type === 'converted';
    const { setNodeRef, isOver } = useDroppable({ id: stage.id, disabled: isConverted });

    const total = stageValueTotal(opps);
    const surface = 'bg-[var(--surface-sunken)]';
    const ring = isOver && !isConverted ? 'ring-2 ring-brand-500' : '';

    return (
        <div ref={setNodeRef} className={`rounded-lg p-2 flex flex-col min-w-[240px] flex-1 ${surface} ${ring}`}>
            <div className="px-1 pb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide truncate text-[var(--ink-muted)]">
                        {stage.name}
                    </span>
                    <span className={`px-1.5 rounded-full text-[10px] font-semibold ${TONE_CLASS[stageTone(stage.type)]}`} style={toneTextStyle(stageTone(stage.type))}>
                        {opps.length}
                    </span>
                </div>
                {showFinancialValues && total > 0 && (
                    <span className="text-xs font-semibold text-[var(--ink-muted)]">
                        {money(total)}
                    </span>
                )}
            </div>

            <div className="space-y-2 flex-1">
                {opps.map(o => (
                    <OpportunityCard key={o.id} opp={o} onCreateReservation={onCreateReservation}
                        canEdit={canEdit} showFinancialValues={showFinancialValues} />
                ))}
                {opps.length === 0 && (
                    <div className="text-[11px] text-center py-6 rounded border border-dashed text-[var(--ink-muted)] border-[var(--surface-border)]">
                        {isConverted ? 'Cards land here on their own' : 'Drop here'}
                    </div>
                )}
            </div>

            {isConverted && (
                <div className="mt-2 px-1 text-[10px] leading-tight text-[var(--ink-muted)]">
                    Automatic — a card arrives when its job exists, not by dragging.
                </div>
            )}
        </div>
    );
}

// The list view: sortable, dense, and honest about age. Everything a board hides.
function PipelineList({ stages, opps, onCreateReservation, canEdit, showFinancialValues }) {
    const [sort, setSort] = useState('age');   // age | value | close

    const stageById = useMemo(() => Object.fromEntries(stages.map(s => [s.id, s])), [stages]);

    const rows = useMemo(() => {
        const list = opps.map(o => ({
            ...o,
            stage: stageById[o.stage_id],
            days: daysSince(o.stage_changed_at),
        }));
        const by = {
            age:   (a, b) => (b.days ?? -1) - (a.days ?? -1),
            value: (a, b) => (Number(b.value) || 0) - (Number(a.value) || 0),
            close: (a, b) => (a.expected_close || '9999').localeCompare(b.expected_close || '9999'),
        };
            return list.sort(by[sort]);
    }, [opps, stageById, sort]);

    const panel = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const head = 'bg-[var(--surface-sunken)] text-[var(--ink-muted)]';
    const sub = 'text-[var(--ink-muted)]';

    const SortBtn = ({ id, children }) => (
        <button onClick={() => setSort(id)}
            className={`px-2 py-1 rounded text-xs font-medium transition
                ${sort === id ? 'bg-brand-600 text-white' : 'text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]'}`}>
            {children}
        </button>
    );

    return (
        <div className={`rounded-lg border shadow-sm overflow-hidden ${panel}`}>
            <div className={`px-3 py-2 flex items-center gap-2 border-b border-[var(--surface-border)]`}>
                <span className={`text-xs ${sub}`}>Sort:</span>
                <SortBtn id="age">Longest in stage</SortBtn>
                {showFinancialValues && <SortBtn id="value">Biggest</SortBtn>}
                <SortBtn id="close">Closing soonest</SortBtn>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className={head}>
                        <tr>
                            {['Client', 'Contact', 'Stage', ...(showFinancialValues ? ['Value'] : []), 'Days in stage', 'Expected close'].map(h => (
                                <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--surface-border)]">
                        {rows.map(o => {
                            // Only OPEN work can go stale. A won or converted deal sitting still
                            // is finished, not rotting — flagging it would cry wolf.
                            const stale = o.stage?.type === 'open' && o.days != null && o.days >= STALE_DAYS;
                            return (
                                <tr key={o.id} className="hover:bg-[var(--surface-sunken)]">
                                    <td className={`px-4 py-2.5 text-sm truncate text-[var(--ink)]`}>
                                        {o.account?.account_name || '—'}
                                    </td>
                                    <td className={`px-4 py-2.5 text-sm truncate ${sub}`}>{o.contact?.name || '—'}</td>
                                    <td className="px-4 py-2.5">
                                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold
                                                          ${TONE_CLASS[stageTone(o.stage?.type)]}`} style={toneTextStyle(stageTone(o.stage?.type))}>
                                            {o.stage?.name || '—'}
                                        </span>
                                    </td>
                                    {showFinancialValues && <td className={`px-4 py-2.5 text-sm font-semibold text-[var(--ink)]`}>
                                        {money(o.value) || <span className={sub}>—</span>}
                                    </td>}
                                    <td className="px-4 py-2.5 text-sm">
                                        {o.days == null ? <span className={sub}>—</span> : (
                                            <span className={stale ? 'text-[var(--flow-orange)] font-semibold' : sub}>
                                                {o.days}d{stale ? ' — stalled' : ''}
                                            </span>
                                        )}
                                    </td>
                                    <td className={`px-4 py-2.5 text-sm ${sub}`}>
                                        {o.expected_close || '—'}
                                        {canEdit && o.stage?.type === 'won' && !o.won_engagement_id && (
                                            <button onClick={() => onCreateReservation(o)}
                                                className="ml-2 px-2 py-0.5 rounded text-[11px] font-semibold bg-brand-600 text-white hover:bg-brand-700">
                                                Create Reservation
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {rows.length === 0 && <div className={`p-8 text-center text-sm ${sub}`}>No opportunities yet.</div>}
        </div>
    );
}

function PipelineView({ userId, theme = 'light', isUser = false }) {
    const { allowed: crmEditAllowed } = useModuleAccess(isUser ? 'crm' : null, 'edit');
    const { allowed: financialViewAllowed } = useModuleAccess(isUser ? 'financial' : null, 'view');
    const canEditCrm = !isUser || crmEditAllowed;
    const showFinancialValues = !isUser || financialViewAllowed;

    const [stages, setStages] = useState([]);
    const [opps, setOpps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(PIPELINE_VIEW_KEY);
            return PIPELINE_VIEWS.includes(saved) ? saved : 'Board';
        } catch { return 'Board'; }
    });
    useEffect(() => {
        try { localStorage.setItem(PIPELINE_VIEW_KEY, viewMode); } catch { /* private mode */ }
    }, [viewMode]);

    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

    const load = useCallback(async () => {
        setError(null);
        const [{ data: s, error: sErr }, { data: o, error: oErr }] = await Promise.all([
            supabaseClient.from('pipeline_stages').select('id, name, rank, type').order('rank'),
            supabaseClient.from('opportunities')
                .select(showFinancialValues ? OPP_SELECT : OPP_SELECT_NO_VALUE)
                .order('created_at', { ascending: false }),
        ]);
        if (sErr || oErr) setError((sErr || oErr).message);
        setStages(s || []);
        setOpps(o || []);
        setLoading(false);
    }, [showFinancialValues]);

    useEffect(() => { load(); }, [load]);

    const byStage = useMemo(() => {
        const typeOf = Object.fromEntries(stages.map(s => [s.id, s.type]));
        const map = new Map(stages.map(s => [s.id, []]));
        opps.forEach(o => {
            if (map.has(o.stage_id)) map.get(o.stage_id).push({ ...o, stageType: typeOf[o.stage_id] });
        });
        return map;
    }, [stages, opps]);

    const onDragEnd = async ({ active, over }) => {
        if (!canEditCrm || !over) return;
        const opp = opps.find(o => o.id === active.id);
        const stage = stages.find(s => s.id === over.id);
        if (!opp || !stage || stage.type === 'converted' || opp.stage_id === over.id) return;
        const payload = stageMovePayload(stage);

        // Optimistic: move the card, then let the server have the last word.
        setOpps(prev => prev.map(o => (o.id === active.id ? { ...o, ...payload } : o)));

        const { error: e } = await supabaseClient
            .from('opportunities')
            .update(payload)
            .eq('id', active.id);

        if (e) {
            setOpps(prev => prev.map(o => (o.id === active.id ? opp : o)));
            setError(e.message);
        }
        await load();   // re-read regardless: triggers may have moved it somewhere else entirely
    };

    // The Won -> Module 0 handoff. An explicit operator action, not magic on drop: creating a
    // job is a business decision. service_type is asked for, never invented — it is NOT NULL
    // on engagements and the live values ("Airport Transfer") are The Ride's vocabulary, not
    // something to guess at.
    const createReservation = async (opp) => {
        if (!canEditCrm) return;
        const serviceType = window.prompt(
            'What service is this? (e.g. Airport Transfer)\n\nThis creates the job and moves the card to your converted column.'
        );
        if (!serviceType || !serviceType.trim()) return;

        try {
            const { data: eng, error: eErr } = await supabaseClient
                .from('engagements')
                .insert([{
                    org_id: opp.org_id,
                    entity_id: opp.entity_id,
                    account_id: opp.account_id,   // links the job back to the client
                    service_type: serviceType.trim(),
                    status: 'requested',
                    created_by: userId,
                }])
                .select('id')
                .single();
            if (eErr) throw eErr;

            // Setting this is all it takes: 018's trigger moves the card to the converted
            // column and marks the sale won. The UI doesn't place the card — the fact does.
            const { error: oErr } = await supabaseClient
                .from('opportunities')
                .update({ won_engagement_id: eng.id })
                .eq('id', opp.id);
            if (oErr) throw oErr;

            await load();
        } catch (err) {
            setError(err.message || String(err));
        }
    };

    const wonUnscheduled = useMemo(() => {
        const wonIds = stages.filter(s => s.type === 'won').map(s => s.id);
        return opps.filter(o => wonIds.includes(o.stage_id) && !o.won_engagement_id);
    }, [stages, opps]);

    if (loading) return <div data-m6-layout="pipeline" data-m6-component="PipelineView" className="p-8 text-center text-gray-500">Loading pipeline…</div>;

    return (
        <div data-m6-layout="pipeline" data-m6-component="PipelineView">
            <div className="mb-4 flex justify-end">
                <div className={`inline-flex rounded-lg border overflow-hidden border-[var(--surface-border)]`}>
                    {PIPELINE_VIEWS.map(m => (
                        <button key={m} onClick={() => setViewMode(m)}
                            className={`px-3 py-2 text-sm font-medium transition
                                ${viewMode === m ? 'bg-brand-600 text-white'
                                    : 'text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]'}`}>
                            {m}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 rounded-lg bg-[var(--flow-orange-fill)] border border-[var(--flow-orange)] text-[var(--flow-orange)] text-sm">
                    {error}
                </div>
            )}

            {/* Money earned and not scheduled — invisible on a five-column board. */}
            {wonUnscheduled.length > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-[var(--flow-orange)]/10 border border-[var(--flow-orange)] text-sm text-[var(--flow-orange)]">
                    <strong>{wonUnscheduled.length}</strong> won{' '}
                    {wonUnscheduled.length === 1 ? 'deal has' : 'deals have'} no job yet
                    {showFinancialValues && <> —{' '}
                        <strong>{money(wonUnscheduled.reduce((s, o) => s + (Number(o.value) || 0), 0))}</strong>{' '}
                        earned and not on anyone’s calendar.
                    </>}
                </div>
            )}

            {viewMode === 'Board' ? (
                <DndContext sensors={sensors} onDragEnd={canEditCrm ? onDragEnd : undefined}>
                    <div tabIndex={0} role="region" aria-label="Pipeline stages, scroll for more stages" className="flex gap-3 overflow-x-auto pb-2">
                        {stages.map(s => (
                            <StageColumn
                                key={s.id}
                                stage={s}
                                opps={byStage.get(s.id) || []}
                                onCreateReservation={createReservation}
                                canEdit={canEditCrm}
                                showFinancialValues={showFinancialValues}
                            />
                        ))}
                    </div>
                </DndContext>
            ) : (
                <PipelineList stages={stages} opps={opps} onCreateReservation={createReservation}
                    canEdit={canEditCrm} showFinancialValues={showFinancialValues} />
            )}

            {stages.length === 0 && (
                <div className="p-10 text-center text-gray-500">
                    No pipeline stages configured for this tenant.
                </div>
            )}
        </div>
    );
}

export default PipelineView;
